import classNames from 'classnames'
import React from 'react'

import { exists } from '~/utils'

import styles from './Box.css'

type Padding<T extends number> = T | [T, T]

export type BoxProps = Pick<
  JSX.IntrinsicElements['div'],
  'id' | 'onAnimationEnd' | 'onClick' | 'onScroll'
> & {
  'data-testid'?: string
  alignSelf?: 'center' | 'default' | 'end' | 'start'
  backgroundColor?: 'grayF3' | 'transparent' | 'white'
  border?: 'darkBold' | 'default' | 'light' | 'normal'
  children?: React.ReactNode
  className?: string
  flex?: 'default' | 'none' | 1
  horizontalAppearance?: 'default' | 'fit-content'
  justifySelf?: 'center' | 'default' | 'end' | 'start'
  padding?: Padding<0 | 1 | 2 | 4 | 8>
  verticalAppearance?: 'default' | 'scroll' | 'stretch-scroll' | 'stretch'
}

export const Box = React.forwardRef<HTMLDivElement, BoxProps>(
  (
    {
      alignSelf = 'default',
      backgroundColor = 'white',
      border = 'default',
      children,
      className,
      'data-testid': dataTestId = 'components-elements-box',
      flex = 'default',
      horizontalAppearance = 'default',
      justifySelf = 'default',
      padding = 0,
      verticalAppearance = 'default',
      ...rest
    },
    ref,
  ) => {
    const { onClick } = rest

    return (
      <div
        ref={ref}
        className={classNames(
          {
            [styles.clickable]: exists(onClick),
          },
          styles[`backgroundColor-${backgroundColor}`],
          {
            [styles[`alignSelf-${alignSelf}`]]: alignSelf !== 'default',
            [styles[`border-${border}`]]: border !== 'default',
            [styles[`flex-${flex}`]]: flex !== 'default',
            [styles[`horizontalAppearance-${horizontalAppearance}`]]:
              horizontalAppearance !== 'default',
            [styles[`justifySelf-${justifySelf}`]]: justifySelf !== 'default',
            [styles[`verticalAppearance-${verticalAppearance}`]]:
              verticalAppearance !== 'default',
          },
          typeof padding === 'number'
            ? {
                [styles[`paddingBlock-${padding}`]]: padding !== 0,
                [styles[`paddingInline-${padding}`]]: padding !== 0,
              }
            : {
                [styles[`paddingBlock-${padding[0]}`]]: padding[0] !== 0,
                [styles[`paddingInline-${padding[1]}`]]: padding[1] !== 0,
              },
          className,
        )}
        data-testid={dataTestId}
        {...rest}
      >
        {children}
      </div>
    )
  },
)
